import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { MiControlledSelectBox } from "@miplatform/components";
import { HTTP_STATUSES, TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Typography, useTheme } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IFloorsDropsFormData } from "../../CustomerPricingDetails/CustomerPricingDetails.types";
import {
  useCustomerPricingDetailsContext,
  useTabFormRegistration,
} from "../../CustomerPricingDetails/CustomerPricingDetailsProvider";
import {
  CategoryFloorsAccordion,
  FloorsDropTypeSelect,
} from "../../components";
import { customerFloorsDropsFormSchema } from "../../schema/customerPricingDetails.schema";
import { useCustomerFloorsDropsTabViewModel } from "./useCustomerFloorsDropsTabViewModel";

export function CustomerFloorsDropsTab() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [selectedFloorId, setSelectedFloorId] = useState<string>("");
  const [selectedDropTypes, setSelectedDropTypes] = useState<string[]>([]);
  const [appliedDropTypeIds, setAppliedDropTypeIds] = useState<string[]>([]);

  const theme = useTheme();

  const { accountDetails, setSaveHandler, setConfirmModalConfig } =
    useCustomerPricingDetailsContext();

  const form = useForm<IFloorsDropsFormData>({
    mode: "onChange",
    defaultValues: {
      maxFloor: {
        maxFloorId: "",
        accountId: "",
      },
      categoryFloorDropTypes: [],
    },
    resolver: zodResolver(customerFloorsDropsFormSchema),
  });
  const { control, reset, getValues, setValue, watch } = form;

  const {
    floors,
    categories,
    categoryDrops,
    dropTypes,
    getFloors,
    getCategories,
    getCategoryDrops,
    getDropTypes,
    saveAccountConfig,
    getCategoryFloorDropTypes,
    saveCategoryFloorDropTypes,
    isLoading,
  } = useCustomerFloorsDropsTabViewModel();

  const maxFloorId = watch("maxFloor.maxFloorId");

  const floorsUpToMaxFloor = useMemo(() => {
    if (!maxFloorId) {
      return [];
    }

    const selectedFloor = floors.find((floor) => floor.id === maxFloorId);
    return selectedFloor
      ? floors.filter(
          (floor) => floor.displayOrder <= selectedFloor.displayOrder
        )
      : [];
  }, [floors, maxFloorId]);

  const appliedCategoryFloorDropTypes = watch("categoryFloorDropTypes");

  // every category with dropRequired must have a drop type applied for each
  // floor up to the selected max floor before the form can be saved
  const allCategoryDropRequirementsMet = useMemo(() => {
    const hasDropForFloor = (categoryId: string, floorId: string) =>
      appliedCategoryFloorDropTypes.some(
        (cfd) => cfd.categoryId === categoryId && cfd.floorId === floorId
      );

    const categoryMeetsRequirement = (categoryId: string) =>
      floorsUpToMaxFloor.every((floor) =>
        hasDropForFloor(categoryId, floor.id)
      );

    return categories
      .filter((category) => category.dropRequired)
      .every((category) => categoryMeetsRequirement(category.id));
  }, [categories, floorsUpToMaxFloor, appliedCategoryFloorDropTypes]);

  useTabFormRegistration(form, isLoading, allCategoryDropRequirementsMet);

  useEffect(() => {
    getFloors();
    getCategories();
    getCategoryDrops();
    getDropTypes();
  }, []);

  useEffect(() => {
    if (!accountDetails) {
      return;
    }
    // load category floor drop types and reset form values
    const loadFloorDropTypes = async () => {
      const response = await getCategoryFloorDropTypes(accountDetails.id);
      const categoryFloorDropTypes =
        response?.status === HTTP_STATUSES.OK ? response.data : [];

      reset({
        maxFloor: {
          maxFloorId: accountDetails.maxFloorId || "",
          accountId: accountDetails.id || "",
        },
        categoryFloorDropTypes,
      });
    };

    loadFloorDropTypes();
  }, [accountDetails]);

  useEffect(() => {
    setSaveHandler(async () => {
      const { maxFloor, categoryFloorDropTypes } = getValues();
      const accountId = accountDetails?.id ?? maxFloor.accountId;
      const { dirtyFields } = form.formState;

      const maxFloorRequest = dirtyFields.maxFloor
        ? saveAccountConfig(maxFloor)
        : null;

      const categoryFloorDropTypesRequest = dirtyFields.categoryFloorDropTypes
        ? saveCategoryFloorDropTypes(accountId, categoryFloorDropTypes)
        : null;

      await Promise.all([maxFloorRequest, categoryFloorDropTypesRequest]);

      reset(getValues());
      return maxFloorRequest ?? categoryFloorDropTypesRequest ?? undefined;
    });
  }, [accountDetails]);

  useEffect(() => {
    if (!accountDetails?.maxFloorId) {
      setConfirmModalConfig(null);
      return;
    }

    setConfirmModalConfig({
      description: `You are about to update maximum floors and assigned drop types for ${
        accountDetails?.name ?? "this account"
      }. Are you sure you would like to continue?`,
    });
  }, [accountDetails, setConfirmModalConfig]);

  useEffect(() => {
    const allowedFloorIds = new Set(
      floorsUpToMaxFloor.map((floor) => floor.id)
    );
    const existingDropTypes = getValues("categoryFloorDropTypes");
    const filteredDropTypes = existingDropTypes.filter((cfd) =>
      allowedFloorIds.has(cfd.floorId)
    );

    if (filteredDropTypes.length !== existingDropTypes.length) {
      setValue("categoryFloorDropTypes", filteredDropTypes, {
        shouldDirty: true,
      });
    }
  }, [floorsUpToMaxFloor, getValues, setValue]);

  const groupedCategoryDropData = useMemo(() => {
    return categories.map((category) => {
      const drops = categoryDrops.filter(
        (drop) => drop.categoryId === category.id
      );
      return {
        category,
        categoryDrops: drops.map((drop) => ({
          ...drop,
          isActive: false,
        })),
      };
    });
  }, [categories, categoryDrops]);

  const maxFloorOptions = floors.map((floor, i) => ({
    label: `${i + 1}`,
    value: floor.id,
  }));

  const handleOpenDropTypeSelect = (
    e: React.MouseEvent<HTMLElement>,
    categoryId: string,
    floorId: string
  ) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedCategoryId(categoryId);
    setSelectedFloorId(floorId);

    const appliedIds = getValues("categoryFloorDropTypes")
      .filter((cfd) => cfd.categoryId === categoryId && cfd.floorId === floorId)
      .map((cfd) => cfd.dropTypeId);

    setSelectedDropTypes(appliedIds);
    setAppliedDropTypeIds(appliedIds);
  };

  const handleCloseDropTypeSelect = () => {
    setSelectedDropTypes([]);
    setAppliedDropTypeIds([]);
    setSelectedCategoryId("");
    setSelectedFloorId("");
    setAnchorEl(null);
  };

  const handleApplyDropTypes = () => {
    const existingDropTypes = getValues("categoryFloorDropTypes");
    const newDropTypes = selectedDropTypes
      .filter(
        (dropTypeId) =>
          !existingDropTypes.some(
            (cfd) =>
              cfd.categoryId === selectedCategoryId &&
              cfd.floorId === selectedFloorId &&
              cfd.dropTypeId === dropTypeId
          )
      )
      .map((dropTypeId) => ({
        categoryId: selectedCategoryId,
        floorId: selectedFloorId,
        dropTypeId,
        dropTypeName: dropTypes.find((dt) => dt.id === dropTypeId)?.name ?? "",
      }));

    if (newDropTypes.length) {
      setValue(
        "categoryFloorDropTypes",
        [...existingDropTypes, ...newDropTypes],
        {
          shouldDirty: true,
        }
      );
    }

    handleCloseDropTypeSelect();
  };

  const handleRemoveDropType = (
    categoryId: string,
    floorId: string,
    dropTypeId: string
  ) => {
    const existingDropTypes = getValues("categoryFloorDropTypes");
    setValue(
      "categoryFloorDropTypes",
      existingDropTypes.filter(
        (cfd) =>
          !(
            cfd.categoryId === categoryId &&
            cfd.floorId === floorId &&
            cfd.dropTypeId === dropTypeId
          )
      ),
      { shouldDirty: true }
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <ConfigAccordionHeader title="Floors">
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography
            variant={TYPOGRAPHY_VARIANTS.BODY2}
            color={theme.palette.text.secondary}
          >
            Enter the maximum number of floors this customer will use.
          </Typography>
          <MiControlledSelectBox
            name="maxFloor.maxFloorId"
            label="Maximum number of floors"
            formControl={control}
            options={maxFloorOptions}
            disabled={isLoading || floors.length === 0 || !categoryDrops.length}
            disableNone
          />
        </Box>
      </ConfigAccordionHeader>
      <ConfigAccordionHeader title="Drops">
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography
            variant={TYPOGRAPHY_VARIANTS.BODY2}
            color={theme.palette.text.secondary}
          >
            {maxFloorId
              ? `Select the drop types that apply to each floor.`
              : "Select a maximum number of floors to manage drop type floor assignments for this customer."}
          </Typography>
          <FloorsDropTypeSelect
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            dropTypes={dropTypes}
            categoryDrops={categoryDrops}
            selectedDropTypes={selectedDropTypes}
            appliedDropTypeIds={appliedDropTypeIds}
            selectedCategoryId={selectedCategoryId}
            setSelectedDropTypes={setSelectedDropTypes}
            handleApplyDropTypes={handleApplyDropTypes}
            handleCloseDropTypeSelect={handleCloseDropTypeSelect}
          />
          {maxFloorId
            ? groupedCategoryDropData?.map(({ category, categoryDrops }) => (
                <CategoryFloorsAccordion
                  key={category.id}
                  category={category}
                  availableFloors={floorsUpToMaxFloor}
                  appliedDropTypes={appliedCategoryFloorDropTypes.filter(
                    (cfd) => cfd.categoryId === category.id
                  )}
                  disabled={isLoading || !maxFloorId}
                  readOnly={false}
                  handleOpenDropTypeSelect={handleOpenDropTypeSelect}
                  handleRemoveDropType={handleRemoveDropType}
                />
              ))
            : null}
        </Box>
      </ConfigAccordionHeader>
    </Box>
  );
}
